import { useCollection } from '@cloudscape-design/collection-hooks';
import Grid from '@risk-smart/themed-cloudscape-components/grid';
import type { SelectProps } from '@risk-smart/themed-cloudscape-components/select';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { defaultPropertyFilterI18nStrings } from '@risksmart-app/components/src/table/propertyFilterI18nStrings';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { drillDownGridDefinition } from 'src/blocks/drill-down-column';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import Select from 'src/components/form/select';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import PageFilterContainer from 'src/components/page-filter-container/PageFilterContainer';
import PropertyFilterPanel from 'src/components/property-filter-panel';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import {
  useGetAppetitesGroupedByImpact,
  useGetRisksFlat,
} from '@/hooks/queries';
import { useGetFormCustomisationByParentTypes } from '@/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useRiskScores } from '@/hooks/useRiskScore';
import { useAddCustomAttributes } from '@/utils/table/hooks/useAddCustomAttributes';
import { useCreateFilterOptions } from '@/utils/table/hooks/useCreateFilterOptions';
import { useCreateFilterProperties } from '@/utils/table/hooks/useCreateFilterProperties';
import { useFiltersFromDBAndUrlHash } from '@/utils/table/hooks/useFiltersFromDBAndUrlHash';
import { emptyFilterQuery } from '@/utils/table/types';
import { addRiskUrl } from '@/utils/urls';

import { useGetFieldConfig } from '../risks/config';
import { useGetLabelledFields } from '../risks/useGetLabelledFields';
import styles from './style.module.scss';
import Tier from './Tier';
import type { DashboardState } from './types';
import { RiskAttribute } from './types';

const initialDashboardState = new Map<number, string | undefined>(
  Array.from({ length: 3 }, (_, i) => [i + 1, undefined])
);

const Page: FC = () => {
  useI18NSummaryHelpContent('risks.dashboardHelp');
  const { t } = useTranslation(['common'], {
    keyPrefix: 'risks',
  });
  const title = t('dashboard_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const navigate = useNavigate();

  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title },
      actions: {
        primary: [
          {
            id: 'create',
            label: t('create_new_button'),
            iconName: 'plus',
            onClick: () => navigate(addRiskUrl()),
            permission: 'insert:risk',
            canHaveAccessAsContributor: true,
          },
        ],
      },
    }),
    [title, t, navigate]
  );

  const [dashboardState, setDashboardState] = useState<DashboardState>(
    initialDashboardState
  );
  const fields = useGetFieldConfig();

  const { data } = useGetRisksFlat({ queryArgs: {} });

  // Select the first tier 1 risk when data loads
  useEffect(() => {
    if (data?.risk) {
      const firstTier1Risk = data.risk.find((r) => r.Tier === 1);
      if (firstTier1Risk) {
        setDashboardState((prev) => {
          const next = new Map(prev);
          next.set(1, firstTier1Risk.Id);

          return next;
        });
      }
    }
  }, [data?.risk]);

  const { data: formCustomisation } = useGetFormCustomisationByParentTypes({
    queryArgs: { parentTypes: [Parent_Type_Enum.EnterpriseRisk] },
  });

  const { scores } = useRiskScores();
  const { data: impactAppetites } = useGetAppetitesGroupedByImpact({
    queryArgs: {},
  });
  const labelledFields = useGetLabelledFields(
    data?.risk,
    scores,
    impactAppetites?.impact
  );
  const { tableFields, tableData } = useAddCustomAttributes({
    fields,
    data: labelledFields,
    customAttributeSchema:
      formCustomisation?.form_configuration?.[0]?.customAttributeSchema ?? null,
    useRelativeDates: true,
  });
  const filteringProperties = useCreateFilterProperties(
    tableFields,
    formCustomisation?.form_configuration ?? null
  );

  // In/out navigation drops the URL hash, so the DB-restore layer (not the hash)
  // is what re-applies the filter — matching the risk register (RSP-1327).
  const { propertyFilter, setPropertyFilter } = useFiltersFromDBAndUrlHash({
    fields: tableFields,
    tableId: 'riskDashboard',
  });

  const { items, actions, propertyFilterProps } = useCollection(tableData, {
    propertyFiltering: {
      filteringProperties,
    },
    selection: {},
  });
  const isImpactsEnabled = useIsModuleEnabled('risk.subModules.impact');
  const fixedFilterOptions = useCreateFilterOptions(
    tableFields,
    tableData,
    propertyFilterProps.filteringOptions
  );

  // useCollection is uncontrolled — push the restored filter in so the cards filter.
  useEffect(() => {
    if (
      propertyFilter &&
      JSON.stringify(propertyFilter) !==
        JSON.stringify(propertyFilterProps.query)
    ) {
      actions.setPropertyFiltering(propertyFilter);
    }
  }, [propertyFilter, actions, propertyFilterProps.query]);

  // TODO: should we removed controlled rating, uncontrolled rating when impacts enabled?
  const options: SelectProps.Option[] = [
    {
      label: t('columns.controlled_rating'),
      value: RiskAttribute.ControlledRating,
    },
    {
      label: t('columns.uncontrolled_rating'),
      value: RiskAttribute.UncontrolledRating,
    },

    {
      label: t('columns.appetite_performance'),
      value: RiskAttribute.AppetitePerformance,
    },
    ...(isImpactsEnabled
      ? [
          {
            label: t('columns.impact_performance'),
            value: RiskAttribute.ImpactPerformance,
          },
        ]
      : []),
    {
      label: t('columns.risk_status'),
      value: RiskAttribute.RiskStatus,
    },
  ];
  const [selectedOption, setSelectOption] = useState<SelectProps.Option>(
    options[0]
  );

  return (
    <PageLayout
      // Object-level toolbar (CUS-152) — flag-gated. The dashboard's only action
      // is "Create new", surfaced as the toolbar's primary icon button
      // (permission-gated by the resolver). Legacy `actions` is the flag-off
      // fallback.
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <Permission
          permission={'insert:risk'}
          canHaveAccessAsContributor={true}
        >
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button variant={'primary'} href={addRiskUrl()}>
              {t('create_new_button')}
            </Button>
          </SpaceBetween>
        </Permission>
      }
      title={title}
    >
      <PageFilterContainer>
        <div className={'flex w-full space-x-2'}>
          <div className={'grow'}>
            <PropertyFilterPanel
              {...propertyFilterProps}
              filteringOptions={fixedFilterOptions}
              query={propertyFilter ?? emptyFilterQuery}
              onChange={({ detail }) => setPropertyFilter(detail)}
              enableTokenGroups={true}
              i18nStrings={defaultPropertyFilterI18nStrings}
              virtualScroll={true}
            />
          </div>
          <div>
            <Select
              selectedOption={selectedOption}
              onChange={(e) => setSelectOption(e.detail.selectedOption)}
              options={options}
            />
          </div>
        </div>
      </PageFilterContainer>
      <div className={styles.dashboard}>
        {/* Reflow rather than scroll — the breakpoints and why they sit where they
            do are documented on `drillDownGridDefinition` (COI-739). */}
        <Grid gridDefinition={drillDownGridDefinition(3)}>
          <Tier
            tier={1}
            selectedRiskAttribute={selectedOption.value as RiskAttribute}
            tierRisks={items}
            dashboardState={dashboardState}
            setDashboardState={setDashboardState}
          />
          <Tier
            tier={2}
            selectedRiskAttribute={selectedOption.value as RiskAttribute}
            tierRisks={items}
            dashboardState={dashboardState}
            setDashboardState={setDashboardState}
          />
          <Tier
            tier={3}
            selectedRiskAttribute={selectedOption.value as RiskAttribute}
            tierRisks={items}
            dashboardState={dashboardState}
            setDashboardState={setDashboardState}
          />
        </Grid>
      </div>
    </PageLayout>
  );
};

export default Page;
